import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Copy,
  ExternalLink,
  FileText,
  MessageSquare,
  Plus,
  Save,
  Scale,
  ShieldCheck,
  Trash2,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

function buildWatchItem() {
  return {
    item_id: '',
    title: '',
    detail: '',
    status: 'watch',
    source_label: '',
    action_label: '',
    link_url: '',
  };
}

function buildTaskItem() {
  return {
    task_id: '',
    title: '',
    detail: '',
    owner: 'Counsel',
    status: 'todo',
    lane: 'custom',
    due_date: '',
    source: 'custom',
  };
}

function buildRequestItem() {
  return {
    request_id: '',
    title: '',
    detail: '',
    status: 'pending',
    channel: 'whatsapp',
    language: 'English',
    due_date: '',
    source: 'custom',
  };
}

function compactText(value, max = 180) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trim()}…`;
}

function ensureList(items, fallbackBuilder) {
  if (!Array.isArray(items) || !items.length) return [];
  return items.map((item) => ({ ...fallbackBuilder(), ...(item || {}) }));
}

function SectionCard({ title, icon: Icon, actions = null, children, soft = false }) {
  return (
    <section
      className="advoq-card"
      style={{
        padding: '18px',
        borderRadius: '24px',
        background: soft ? 'color-mix(in srgb, var(--bg-elevated) 88%, var(--accent-primary) 3%)' : 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '12px',
              background: 'var(--bg-elevated)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-primary)',
              flexShrink: 0,
            }}
          >
            <Icon size={16} />
          </span>
          <div>
            <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '17px', lineHeight: 1.3 }}>
              {title}
            </h3>
          </div>
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

function MetricBar({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px' }}>
      {items.map((item) => (
        <div
          key={item.label}
          className="advoq-card"
          style={{
            padding: '18px',
            borderRadius: '20px',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '8px' }}>
            {item.label}
          </div>
          <div style={{ color: 'var(--text-primary)', fontSize: '28px', fontWeight: 800, letterSpacing: '-0.04em' }}>
            {item.value}
          </div>
          {item.note ? (
            <div style={{ marginTop: '7px', color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.55 }}>
              {item.note}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function Chip({ children, tone = 'default' }) {
  const background = tone === 'warn'
    ? 'rgba(180, 83, 9, 0.12)'
    : tone === 'success'
      ? 'rgba(5, 150, 105, 0.12)'
      : 'rgba(15,118,110,0.08)';
  const color = tone === 'warn'
    ? '#b45309'
    : tone === 'success'
      ? '#047857'
      : 'var(--accent-primary)';
  return (
    <span
      className="app-page-chip"
      style={{
        background,
        color,
      }}
    >
      {children}
    </span>
  );
}

function EmptyState({ text }) {
  return (
    <div style={{ color: 'var(--text-muted)', fontSize: '14px', lineHeight: 1.7 }}>
      {text}
    </div>
  );
}

function MatterOperationsWorkspace({ matterId, operations, saving = false, onSave }) {
  const courtWatch = operations?.court_watch || {};
  const filingFactory = operations?.filing_factory || {};
  const teamCoordination = operations?.team_coordination || {};
  const clientLoop = operations?.client_loop || {};
  const playbook = operations?.playbook || {};
  const state = operations?.state || {};

  const [trackingMode, setTrackingMode] = useState('manual');
  const [watchNotes, setWatchNotes] = useState('');
  const [watchItems, setWatchItems] = useState([]);
  const [targetFiling, setTargetFiling] = useState('');
  const [bundleStatus, setBundleStatus] = useState('scoping');
  const [filingNotes, setFilingNotes] = useState('');
  const [teamTasks, setTeamTasks] = useState([]);
  const [clientRequests, setClientRequests] = useState([]);
  const [opsNotes, setOpsNotes] = useState('');

  useEffect(() => {
    setTrackingMode(courtWatch.tracking_mode || state?.court_watch?.tracking_mode || 'manual');
    setWatchNotes(courtWatch.notes || state?.court_watch?.notes || '');
    setWatchItems(ensureList(courtWatch.tracking_items || state?.court_watch?.items, buildWatchItem));
    setTargetFiling(filingFactory.target_filing || state?.filing_factory?.target_filing || '');
    setBundleStatus(filingFactory.bundle_status || state?.filing_factory?.bundle_status || 'scoping');
    setFilingNotes(filingFactory.notes || state?.filing_factory?.notes || '');
    setTeamTasks(ensureList(teamCoordination.tasks || state?.team_tasks, buildTaskItem));
    setClientRequests(ensureList(clientLoop.requests || state?.client_requests, buildRequestItem));
    setOpsNotes(state?.notes || '');
  }, [clientLoop.requests, courtWatch.notes, courtWatch.tracking_items, courtWatch.tracking_mode, filingFactory.bundle_status, filingFactory.notes, filingFactory.target_filing, state]);

  const metrics = useMemo(() => {
    const openTasks = teamTasks.filter((item) => item.status !== 'done').length;
    const pendingClientRequests = clientRequests.filter((item) => item.status !== 'done').length;
    return [
      {
        label: 'Court watch',
        value: watchItems.length || '0',
        note: courtWatch.status_label || 'Manual watch',
      },
      {
        label: 'Bundle',
        value: filingFactory.bundle_label || 'Scoping',
        note: targetFiling || 'Lock the filing lane.',
      },
      {
        label: 'Team queue',
        value: String(openTasks),
        note: teamCoordination.summary || 'Track active work here.',
      },
      {
        label: 'Client loop',
        value: String(pendingClientRequests),
        note: clientLoop.channel_suggestion || 'Keep the client in sync.',
      },
    ];
  }, [clientLoop.channel_suggestion, courtWatch.status_label, filingFactory.bundle_label, targetFiling, teamCoordination.summary, teamTasks, clientRequests, watchItems.length]);

  const updateWatchItem = (index, key, value) => {
    setWatchItems((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [key]: value } : item
    )));
  };

  const updateTeamTask = (index, key, value) => {
    setTeamTasks((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [key]: value } : item
    )));
  };

  const updateClientRequest = (index, key, value) => {
    setClientRequests((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [key]: value } : item
    )));
  };

  const saveCourtWatch = () => onSave({
    court_watch: {
      tracking_mode: trackingMode,
      notes: watchNotes,
      items: watchItems,
    },
  });

  const saveFilingFactory = () => onSave({
    filing_factory: {
      target_filing: targetFiling,
      bundle_status: bundleStatus,
      notes: filingNotes,
    },
  });

  const saveTeamTasks = () => onSave({ team_tasks: teamTasks });

  const saveClientRequests = () => onSave({ client_requests: clientRequests });

  const saveOpsNotes = () => onSave({ notes: opsNotes });

  const copyClientUpdate = async () => {
    const updateDraft = clientLoop.update_draft || {};
    const content = [
      updateDraft.headline || clientLoop.headline || 'Matter update',
      '',
      updateDraft.summary || clientLoop.summary || '',
      '',
      ...(updateDraft.bullets || []).map((item) => `- ${item}`),
    ].filter(Boolean).join('\n');

    try {
      if (!navigator?.clipboard?.writeText) {
        throw new Error('Clipboard unavailable');
      }
      await navigator.clipboard.writeText(content);
      toast.success('Client update copied');
    } catch (error) {
      toast.error('Unable to copy client update');
    }
  };

  return (
    <div data-testid="matter-ops-view" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <SectionCard
        title="Matter Ops"
        icon={ClipboardList}
        soft
        actions={(
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {matterId ? <Chip>{matterId}</Chip> : null}
            {courtWatch.leading_issue ? <Chip>{courtWatch.leading_issue}</Chip> : null}
            {playbook.label ? <Chip>{playbook.label}</Chip> : null}
            {clientLoop.bilingual_ready ? (
              <Chip tone="success">Bilingual-ready</Chip>
            ) : null}
          </div>
        )}
      >
        <div style={{ color: 'var(--text-primary)', fontSize: '15px', lineHeight: 1.75 }}>
          {operations?.headline || 'Matter Ops keeps court-watch, filing execution, team follow-through, and client updates inside the same matter.'}
        </div>
        <MetricBar items={metrics} />
      </SectionCard>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px', alignItems: 'start' }}>
        <SectionCard
          title="Court Watch"
          icon={CalendarDays}
          actions={(
            <button type="button" className="advoq-btn-primary" onClick={saveCourtWatch} disabled={saving}>
              <Save size={14} />
              Save watch
            </button>
          )}
        >
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Chip>{courtWatch.status_label || 'Manual watch'}</Chip>
            <Chip>{trackingMode.replace(/_/g, ' ')}</Chip>
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
            {courtWatch.sync_note || 'Keep official listing and order movement visible here.'}
          </div>
          <label style={{ display: 'grid', gap: '8px' }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Tracking mode</span>
            <select className="advoq-input" value={trackingMode} onChange={(event) => setTrackingMode(event.target.value)}>
              <option value="manual">Manual</option>
              <option value="assisted">Assisted</option>
              <option value="portal_ready">Portal-ready</option>
            </select>
          </label>
          <div style={{ display: 'grid', gap: '10px' }}>
            <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>Official channels</div>
            {(courtWatch.official_channels || []).length ? (
              <div style={{ display: 'grid', gap: '10px' }}>
                {courtWatch.official_channels.map((item) => (
                  <a
                    key={item.id}
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      textDecoration: 'none',
                      borderRadius: '16px',
                      border: '1px solid var(--border-subtle)',
                      background: 'var(--bg-elevated)',
                      padding: '12px 14px',
                      display: 'grid',
                      gap: '6px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                      <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>{item.label}</div>
                      <ExternalLink size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{item.trust_label}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>{item.detail}</div>
                  </a>
                ))}
              </div>
            ) : (
              <EmptyState text="No official channels are mapped yet." />
            )}
          </div>
          <div style={{ display: 'grid', gap: '10px' }}>
            <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>Tracking items</div>
            {watchItems.length ? (
              <div style={{ display: 'grid', gap: '12px' }}>
                {watchItems.map((item, index) => (
                  <div key={`${item.item_id || item.title || 'watch'}_${index}`} style={{ borderRadius: '18px', border: '1px solid var(--border-subtle)', padding: '14px', display: 'grid', gap: '10px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 150px auto', gap: '10px' }}>
                      <input
                        className="advoq-input"
                        placeholder="Watch item title"
                        value={item.title}
                        onChange={(event) => updateWatchItem(index, 'title', event.target.value)}
                      />
                      <select className="advoq-input" value={item.status} onChange={(event) => updateWatchItem(index, 'status', event.target.value)}>
                        <option value="watch">Watch</option>
                        <option value="action_needed">Action needed</option>
                        <option value="synced">Synced</option>
                      </select>
                      <button type="button" className="app-chat-button" onClick={() => setWatchItems((current) => current.filter((_, itemIndex) => itemIndex !== index))}>
                        <Trash2 size={14} />
                        Remove
                      </button>
                    </div>
                    <textarea
                      className="advoq-input"
                      rows={2}
                      placeholder="What changed, what needs checking, and why it matters."
                      value={item.detail}
                      onChange={(event) => updateWatchItem(index, 'detail', event.target.value)}
                      style={{ resize: 'vertical' }}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                      <input
                        className="advoq-input"
                        placeholder="Source label"
                        value={item.source_label}
                        onChange={(event) => updateWatchItem(index, 'source_label', event.target.value)}
                      />
                      <input
                        className="advoq-input"
                        placeholder="Action label"
                        value={item.action_label}
                        onChange={(event) => updateWatchItem(index, 'action_label', event.target.value)}
                      />
                      <input
                        className="advoq-input"
                        placeholder="Optional link URL"
                        value={item.link_url}
                        onChange={(event) => updateWatchItem(index, 'link_url', event.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text="No court-watch items yet. Add the first manual watch below." />
            )}
            <button type="button" className="app-chat-button" onClick={() => setWatchItems((current) => [...current, buildWatchItem()])}>
              <Plus size={14} />
              Add watch item
            </button>
          </div>
          <label style={{ display: 'grid', gap: '8px' }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Watch notes</span>
            <textarea
              className="advoq-input"
              rows={4}
              placeholder="Registry call notes, portal findings, cause-list reminders, scrutiny observations."
              value={watchNotes}
              onChange={(event) => setWatchNotes(event.target.value)}
              style={{ resize: 'vertical' }}
            />
          </label>
        </SectionCard>

        <SectionCard
          title="Filing Factory"
          icon={FileText}
          actions={(
            <button type="button" className="advoq-btn-primary" onClick={saveFilingFactory} disabled={saving}>
              <Save size={14} />
              Save filing lane
            </button>
          )}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 180px', gap: '10px' }}>
            <input
              className="advoq-input"
              placeholder="Target filing"
              value={targetFiling}
              onChange={(event) => setTargetFiling(event.target.value)}
            />
            <select className="advoq-input" value={bundleStatus} onChange={(event) => setBundleStatus(event.target.value)}>
              <option value="scoping">Scoping</option>
              <option value="building">Building</option>
              <option value="review">Review</option>
              <option value="ready_to_file">Ready to file</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Chip>{filingFactory.bundle_label || compactText(bundleStatus.replace(/_/g, ' '), 24)}</Chip>
            {playbook.target_filing ? <Chip>{playbook.target_filing}</Chip> : null}
          </div>
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>Checklist</div>
            {(filingFactory.checklist || []).length ? (
              filingFactory.checklist.map((item) => (
                <div key={item.label} style={{ display: 'grid', gap: '4px', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                    <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>{item.label}</div>
                    <Chip tone={item.status === 'Ready' ? 'success' : item.status === 'Missing' || item.status === 'Thin' || item.status === 'Needs support' || item.status === 'Needs work' ? 'warn' : 'default'}>
                      {item.status}
                    </Chip>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65 }}>{item.detail}</div>
                </div>
              ))
            ) : (
              <EmptyState text="No filing checklist yet." />
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
            <div style={{ display: 'grid', gap: '10px' }}>
              <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>Annexure map</div>
              {(filingFactory.annexures || []).length ? (
                filingFactory.annexures.map((item) => (
                  <div key={item.label} style={{ borderRadius: '16px', border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)', padding: '12px', display: 'grid', gap: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                      <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700 }}>{item.label}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{item.status}</div>
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>{item.detail}</div>
                  </div>
                ))
              ) : (
                <EmptyState text="No annexure map yet." />
              )}
            </div>
            <div style={{ display: 'grid', gap: '10px' }}>
              <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>Defect radar</div>
              {(filingFactory.defect_radar || []).length ? (
                filingFactory.defect_radar.map((item, index) => (
                  <div key={`${item.title}_${index}`} style={{ borderRadius: '16px', border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)', padding: '12px', display: 'grid', gap: '6px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <AlertTriangle size={14} style={{ color: item.severity === 'High' ? '#b45309' : 'var(--text-muted)', flexShrink: 0 }} />
                      <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700 }}>{item.title}</div>
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{item.severity}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>{item.detail}</div>
                  </div>
                ))
              ) : (
                <EmptyState text="No defect radar yet." />
              )}
            </div>
          </div>
          <div style={{ display: 'grid', gap: '10px' }}>
            <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>Output bundle</div>
            {(filingFactory.output_bundle || []).length ? (
              filingFactory.output_bundle.map((item) => (
                <div key={item.title} style={{ display: 'grid', gap: '3px' }}>
                  <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700 }}>{item.title}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>{item.detail}</div>
                </div>
              ))
            ) : (
              <EmptyState text="No output bundle yet." />
            )}
          </div>
          <textarea
            className="advoq-input"
            rows={4}
            placeholder="Annexure gaps, page-numbering notes, scrutiny defects, service issues."
            value={filingNotes}
            onChange={(event) => setFilingNotes(event.target.value)}
            style={{ resize: 'vertical' }}
          />
        </SectionCard>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px', alignItems: 'start' }}>
        <SectionCard
          title="Team Coordination"
          icon={Users}
          actions={(
            <button type="button" className="advoq-btn-primary" onClick={saveTeamTasks} disabled={saving}>
              <Save size={14} />
              Save tasks
            </button>
          )}
        >
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
            {teamCoordination.summary || 'Keep owner, status, and lane visible inside the matter.'}
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Chip>{teamCoordination.counts?.todo || 0} todo</Chip>
            <Chip>{teamCoordination.counts?.in_progress || 0} in progress</Chip>
            <Chip tone="warn">{teamCoordination.counts?.blocked || 0} blocked</Chip>
            <Chip tone="success">{teamCoordination.counts?.done || 0} done</Chip>
          </div>
          {teamTasks.length ? (
            <div style={{ display: 'grid', gap: '12px' }}>
              {teamTasks.map((item, index) => (
                <div key={`${item.task_id || item.title || 'task'}_${index}`} style={{ borderRadius: '18px', border: '1px solid var(--border-subtle)', padding: '14px', display: 'grid', gap: '10px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 150px auto', gap: '10px' }}>
                    <input
                      className="advoq-input"
                      placeholder="Task title"
                      value={item.title}
                      onChange={(event) => updateTeamTask(index, 'title', event.target.value)}
                    />
                    <select className="advoq-input" value={item.status} onChange={(event) => updateTeamTask(index, 'status', event.target.value)}>
                      <option value="todo">Todo</option>
                      <option value="in_progress">In progress</option>
                      <option value="blocked">Blocked</option>
                      <option value="done">Done</option>
                    </select>
                    <button type="button" className="app-chat-button" onClick={() => setTeamTasks((current) => current.filter((_, itemIndex) => itemIndex !== index))}>
                      <Trash2 size={14} />
                      Remove
                    </button>
                  </div>
                  <textarea
                    className="advoq-input"
                    rows={2}
                    placeholder="What exactly needs to happen?"
                    value={item.detail}
                    onChange={(event) => updateTeamTask(index, 'detail', event.target.value)}
                    style={{ resize: 'vertical' }}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
                    <input
                      className="advoq-input"
                      placeholder="Owner"
                      value={item.owner}
                      onChange={(event) => updateTeamTask(index, 'owner', event.target.value)}
                    />
                    <input
                      className="advoq-input"
                      placeholder="Lane"
                      value={item.lane}
                      onChange={(event) => updateTeamTask(index, 'lane', event.target.value)}
                    />
                    <input
                      className="advoq-input"
                      placeholder="Due date"
                      value={item.due_date}
                      onChange={(event) => updateTeamTask(index, 'due_date', event.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState text="No tasks visible yet. Add the first chamber item below." />
          )}
          <button type="button" className="app-chat-button" onClick={() => setTeamTasks((current) => [...current, buildTaskItem()])}>
            <Plus size={14} />
            Add task
          </button>
          {(teamCoordination.review_queue || []).length ? (
            <div style={{ display: 'grid', gap: '8px' }}>
              <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>Review queue</div>
              {teamCoordination.review_queue.map((item, index) => (
                <div key={`${item.task_id || item.title || 'queue'}_${index}`} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <ClipboardList size={14} style={{ color: 'var(--text-muted)', marginTop: '3px', flexShrink: 0 }} />
                  <div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700 }}>{item.title}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>{item.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </SectionCard>

        <SectionCard
          title="Client Loop"
          icon={MessageSquare}
          actions={(
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" className="app-chat-button" onClick={copyClientUpdate}>
                <Copy size={14} />
                Copy update
              </button>
              <button type="button" className="advoq-btn-primary" onClick={saveClientRequests} disabled={saving}>
                <Save size={14} />
                Save requests
              </button>
            </div>
          )}
        >
          <div style={{ color: 'var(--text-primary)', fontSize: '15px', lineHeight: 1.7 }}>
            {clientLoop.headline || 'Keep the client updated in plain language.'}
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.75 }}>
            {clientLoop.summary || 'Turn the legal posture into a clean update and a short request list.'}
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {clientLoop.channel_suggestion ? <Chip>{clientLoop.channel_suggestion}</Chip> : null}
            {clientLoop.bilingual_ready ? <Chip tone="success">Jolly-backed bilingual context available</Chip> : null}
          </div>
          <div style={{ borderRadius: '18px', border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)', padding: '14px', display: 'grid', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle2 size={15} style={{ color: 'var(--accent-primary)' }} />
              <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>
                WhatsApp-ready draft
              </div>
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
              {clientLoop.update_draft?.headline || clientLoop.headline}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
              {clientLoop.update_draft?.summary || clientLoop.summary}
            </div>
            {(clientLoop.update_draft?.bullets || []).length ? (
              <div style={{ display: 'grid', gap: '6px' }}>
                {clientLoop.update_draft.bullets.map((item, index) => (
                  <div key={`${item}_${index}`} style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>
                    {item}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          {clientRequests.length ? (
            <div style={{ display: 'grid', gap: '12px' }}>
              {clientRequests.map((item, index) => (
                <div key={`${item.request_id || item.title || 'request'}_${index}`} style={{ borderRadius: '18px', border: '1px solid var(--border-subtle)', padding: '14px', display: 'grid', gap: '10px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 170px auto', gap: '10px' }}>
                    <input
                      className="advoq-input"
                      placeholder="Request title"
                      value={item.title}
                      onChange={(event) => updateClientRequest(index, 'title', event.target.value)}
                    />
                    <select className="advoq-input" value={item.status} onChange={(event) => updateClientRequest(index, 'status', event.target.value)}>
                      <option value="pending">Pending</option>
                      <option value="waiting_on_client">Waiting on client</option>
                      <option value="ready">Ready</option>
                      <option value="done">Done</option>
                    </select>
                    <button type="button" className="app-chat-button" onClick={() => setClientRequests((current) => current.filter((_, itemIndex) => itemIndex !== index))}>
                      <Trash2 size={14} />
                      Remove
                    </button>
                  </div>
                  <textarea
                    className="advoq-input"
                    rows={2}
                    placeholder="What do we need from the client, and why?"
                    value={item.detail}
                    onChange={(event) => updateClientRequest(index, 'detail', event.target.value)}
                    style={{ resize: 'vertical' }}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
                    <select className="advoq-input" value={item.channel} onChange={(event) => updateClientRequest(index, 'channel', event.target.value)}>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="email">Email</option>
                      <option value="call">Call</option>
                    </select>
                    <input
                      className="advoq-input"
                      placeholder="Language"
                      value={item.language}
                      onChange={(event) => updateClientRequest(index, 'language', event.target.value)}
                    />
                    <input
                      className="advoq-input"
                      placeholder="Due date"
                      value={item.due_date}
                      onChange={(event) => updateClientRequest(index, 'due_date', event.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState text="No client requests visible yet. Add the first request below." />
          )}
          <button type="button" className="app-chat-button" onClick={() => setClientRequests((current) => [...current, buildRequestItem()])}>
            <Plus size={14} />
            Add client request
          </button>
        </SectionCard>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', alignItems: 'start' }}>
        <SectionCard title="Vertical Playbook" icon={Scale} soft>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {playbook.label ? <Chip>{playbook.label}</Chip> : null}
            {playbook.phase_label ? <Chip>{playbook.phase_label}</Chip> : null}
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
            {playbook.target_filing || targetFiling || 'Lock the live filing or hearing lane first.'}
          </div>
          <div style={{ display: 'grid', gap: '12px' }}>
            <div>
              <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>Now</div>
              {(playbook.now || []).length ? playbook.now.map((item, index) => (
                <div key={`${item}_${index}`} style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65, marginBottom: '6px' }}>
                  {item}
                </div>
              )) : <EmptyState text="No immediate playbook items yet." />}
            </div>
            <div>
              <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>Next</div>
              {(playbook.next || []).length ? playbook.next.map((item, index) => (
                <div key={`${item}_${index}`} style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65, marginBottom: '6px' }}>
                  {item}
                </div>
              )) : <EmptyState text="No next-step playbook items yet." />}
            </div>
            <div>
              <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>Watchouts</div>
              {(playbook.watchouts || []).length ? playbook.watchouts.map((item, index) => (
                <div key={`${item}_${index}`} style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65, marginBottom: '6px' }}>
                  {item}
                </div>
              )) : <EmptyState text="No watchouts yet." />}
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="Ops Notes"
          icon={ShieldCheck}
          actions={(
            <button type="button" className="advoq-btn-primary" onClick={saveOpsNotes} disabled={saving}>
              <Save size={14} />
              Save notes
            </button>
          )}
        >
          <textarea
            className="advoq-input"
            rows={10}
            placeholder="Morning cause-list pulse, partner review notes, filing defects, hearing follow-up, registry learnings."
            value={opsNotes}
            onChange={(event) => setOpsNotes(event.target.value)}
            style={{ resize: 'vertical' }}
          />
          <div style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.65 }}>
            Keep one shared operations note per matter so court reality, filing readiness, and team movement do not split across WhatsApp, calls, and personal notes.
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

export default MatterOperationsWorkspace;
